import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Save, RefreshCw, Send } from "lucide-react";
import { api, API } from "@/lib/api";
import { btn, gold, inp } from "./Queue";

const CATEGORIES = ["Original Paintings", "Prints", "Custom Commissions", "Scenic", "Animals", "Flowers", "Abstract", "Charcoal Artwork"];

function ThemeRow({ t, collections, onSaved, onDelete }) {
  const [f, setF] = useState(t);
  const toggleCat = (c) => setF({ ...f, categories: f.categories.includes(c) ? f.categories.filter(x => x !== c) : [...f.categories, c] });
  const save = async () => {
    const body = { name: f.name, audience: f.audience, angle: f.angle || "", categories: f.categories, artwork_ids: f.artwork_ids || [], collection_id: f.collection_id || null, active: !!f.active };
    try {
      if (t.id) await api.patch(`/admin/social/themes/${t.id}`, body); else await api.post("/admin/social/themes", body);
      toast.success("Theme saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="border border-white/10 p-4 grid md:grid-cols-2 gap-3" data-testid={`theme-row-${t.id || "new"}`}>
      <input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} placeholder="Theme name" className={inp} data-testid="theme-name" />
      <select value={f.audience} onChange={e => setF({ ...f, audience: e.target.value })} className={inp} data-testid="theme-audience"><option value="business">Business (restaurants, hotels, offices)</option><option value="household">Household (gifts & decor)</option></select>
      <textarea value={f.angle || ""} onChange={e => setF({ ...f, angle: e.target.value })} rows={2} placeholder="Angle / what this theme is about (guides the writer)" className={`${inp} md:col-span-2`} data-testid="theme-angle" />
      <div className="flex flex-wrap gap-1 md:col-span-2">
        {CATEGORIES.map(c => <button key={c} type="button" onClick={() => toggleCat(c)} className={`px-2 py-1 text-[11px] border ${f.categories.includes(c) ? "border-[#C77746] text-[#C77746]" : "border-white/15 text-white/50"}`} data-testid={`theme-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}>{c}</button>)}
      </div>
      <select value={f.collection_id || ""} onChange={e => setF({ ...f, collection_id: e.target.value || null })} className={inp} data-testid="theme-collection">
        <option value="">Link → {f.audience === "business" ? "/business (all collections)" : "/gallery"}</option>
        {collections.map(c => <option key={c.id} value={c.id}>Link → Business Collection: {c.name}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.active} onChange={e => setF({ ...f, active: e.target.checked })} data-testid="theme-active" /> Active (used in weekly batches)</label>
      <div className="flex gap-2 md:col-span-2">
        <button type="button" className={gold} onClick={save} data-testid="theme-save"><Save size={12} /> Save</button>
        {t.id && <button type="button" className={`${btn} hover:border-red-400 hover:text-red-400`} onClick={onDelete} data-testid={`theme-delete-${t.id}`}><Trash2 size={12} /></button>}
      </div>
    </div>
  );
}

export function Themes({ themes, reload }) {
  const [collections, setCollections] = useState([]);
  const [adding, setAdding] = useState(false);
  useEffect(() => { api.get("/admin/business/collections").then(r => setCollections(r.data)).catch(() => {}); }, []);
  const del = async (t) => { if (window.confirm(`Delete theme "${t.name}"?`)) { await api.delete(`/admin/social/themes/${t.id}`); reload(); } };
  return (
    <div className="space-y-4" data-testid="social-themes">
      <div className="flex justify-between items-center"><div className="overline text-white/40">Every post is tied to one theme. Links carry UTM tags so orders can be attributed.</div>
        <button type="button" className={gold} onClick={() => setAdding(true)} data-testid="theme-add"><Plus size={12} /> New theme</button></div>
      {adding && <ThemeRow t={{ name: "", audience: "household", angle: "", categories: [], artwork_ids: [], collection_id: null, active: true }} collections={collections} onSaved={() => { setAdding(false); reload(); }} />}
      {themes.map(t => <ThemeRow key={t.id} t={t} collections={collections} onSaved={reload} onDelete={() => del(t)} />)}
    </div>
  );
}

const Rank = ({ title, rows, labelKey = "key" }) => (
  <div className="border border-white/10 p-4" data-testid={`rank-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="overline text-[#C77746] mb-2">{title}</div>
    {(!rows || rows.length === 0) ? <div className="text-xs text-white/40">No data yet</div> : rows.slice(0, 5).map((r, i) => (
      <div key={String(r.key)} className="flex justify-between text-sm py-1 border-b border-white/5"><span>{i + 1}. {r[labelKey] ?? r.key}{title === "Posting hours" ? ":00" : ""}</span><span className="text-white/50">{r.avg_score} avg · {r.posts} post{r.posts === 1 ? "" : "s"}</span></div>
    ))}
  </div>
);

export function Playbook() {
  const [pb, setPb] = useState(null);
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(null);
  const load = () => { api.get("/admin/social/playbook").then(r => setPb(r.data)); api.get("/admin/social/reports").then(r => setReports(r.data)); };
  useEffect(load, []);
  const run = async (path, key) => {
    setBusy(key);
    try { await api.post(path); toast.success(key === "pb" ? "Playbook updated" : "Report generated"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(null); }
  };
  if (!pb) return null;
  return (
    <div className="space-y-8" data-testid="social-playbook">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div className="overline text-white/40">Learnings from {pb.sample || 0} published post{pb.sample === 1 ? "" : "s"}{pb.computed_at ? ` · updated ${new Date(pb.computed_at).toLocaleString()}` : ""}</div>
        <div className="flex gap-2">
          <button type="button" className={btn} disabled={!!busy} onClick={() => run("/admin/social/playbook/recompute", "pb")} data-testid="playbook-recompute"><RefreshCw size={12} className={busy === "pb" ? "animate-spin" : ""} /> Pull stats & re-rank</button>
          <button type="button" className={gold} disabled={!!busy} onClick={() => run("/admin/social/reports/generate", "rep")} data-testid="report-generate"><Send size={12} /> {busy === "rep" ? "Writing…" : "Generate 48h report now"}</button>
        </div>
      </div>
      <div className="border border-[#C77746]/40 p-5" data-testid="playbook-insights">
        <div className="overline text-[#C77746] mb-3">Current playbook</div>
        <ul className="space-y-2 text-sm text-white/80">{(pb.insights || []).map((s, i) => <li key={i}>• {s}</li>)}</ul>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <Rank title="Themes" rows={pb.themes} labelKey="label" /><Rank title="Hooks" rows={pb.hooks} /><Rank title="Posting hours" rows={pb.hours} />
        <Rank title="Audiences" rows={pb.audiences} /><Rank title="Channels" rows={pb.channels} /><Rank title="Formats" rows={pb.formats} />
      </div>
      <div data-testid="social-reports">
        <div className="overline text-white/40 mb-3">48-hour reports</div>
        {reports.length === 0 && <p className="text-white/40 text-sm">No reports yet — one is generated automatically every 48 hours.</p>}
        {reports.map(r => (
          <div key={r.id} className="border border-white/10 p-5 mb-3" data-testid={`report-${r.id}`}>
            <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">{new Date(r.created_at).toLocaleString()} · {r.published_count} published · {r.awaiting_approval} awaiting approval</div>
            <div className="font-serif text-xl mb-3">{r.headline}</div>
            <div className="grid md:grid-cols-3 gap-4 text-sm">
              {[["What performed", r.performed, "text-emerald-300"], ["What didn't", r.underperformed, "text-red-300"], ["Planned next", r.planned, "text-[#C77746]"]].map(([h, items, c]) => (
                <div key={h}><div className={`overline mb-2 ${c}`}>{h}</div><ul className="space-y-1 text-white/70">{(items || []).length ? items.map((x, i) => <li key={i}>• {x}</li>) : <li className="text-white/30">—</li>}</ul></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function Chat({ onPostsCreated }) {
  const [sid] = useState(() => localStorage.getItem("fabj_social_chat") || (() => { const s = crypto.randomUUID(); localStorage.setItem("fabj_social_chat", s); return s; })());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { api.get(`/admin/social/chat/${sid}`).then(r => setMsgs(r.data.map(m => ({ role: m.role, content: m.content })))); }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async (e) => {
    e.preventDefault();
    const q = text.trim(); if (!q || busy) return;
    setText(""); setBusy(true);
    setMsgs(m => [...m, { role: "user", content: q }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/admin/social/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sid, message: q }) });
      const reader = res.body.getReader(); const dec = new TextDecoder(); let buf = "";
      for (;;) {
        const { value, done } = await reader.read(); if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n"); buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.delta) setMsgs(m => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + ev.delta }; return n; });
          if (ev.tool) setMsgs(m => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], tool: ev.tool }; return n; });
          if (ev.done && ev.post_ids?.length) onPostsCreated();
        }
      }
    } catch { toast.error("Chat failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-white/10 flex flex-col h-[70vh]" data-testid="social-chat">
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {msgs.length === 0 && <p className="text-white/40 text-sm">Try: “5 spring gift posts for Facebook” · “carousel for the Flowers & Scenic collection” · “a business post about hotel lobbies”</p>}
        {msgs.map((m, i) => (
          <div key={i} className={`max-w-[85%] ${m.role === "user" ? "ml-auto" : ""}`} data-testid={`chat-msg-${m.role}-${i}`}>
            <div className={`px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${m.role === "user" ? "bg-[#C77746] text-black" : "bg-white/[0.06] text-white/85"}`}>{m.content || (busy && i === msgs.length - 1 ? "…" : "")}</div>
            {m.tool && <div className="text-[11px] text-emerald-300 mt-1" data-testid={`chat-tool-${i}`}>✓ {m.tool.created} draft{m.tool.created === 1 ? "" : "s"} added to the queue ({m.tool.theme} · {m.tool.channel})</div>}
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-white/10">
        <input value={text} onChange={e => setText(e.target.value)} placeholder="Ask for posts…" className={`${inp} flex-1`} data-testid="chat-input" disabled={busy} />
        <button type="submit" className={gold} disabled={busy || !text.trim()} data-testid="chat-send"><Send size={12} /> Send</button>
      </form>
    </div>
  );
}

export function Settings({ settings, onSaved }) {
  const [f, setF] = useState(settings);
  useEffect(() => setF(settings), [settings]);
  const save = async () => {
    try { await api.patch("/admin/social/settings", { timezone: f.timezone, post_hour: Number(f.post_hour), site_url: f.site_url, fb_page_id: f.fb_page_id, fb_page_token: f.fb_page_token, posts_per_week: Number(f.posts_per_week) }); toast.success("Settings saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="social-settings">
      <div className={`border p-4 text-sm ${settings.fb_connected ? "border-emerald-500/40 text-emerald-300" : "border-white/10 text-white/60"}`} data-testid="fb-status">
        {settings.fb_connected ? "Facebook Page connected — approved posts publish automatically at their scheduled time." : "Facebook not connected — approved Facebook posts wait in Ready to Post until you connect your Page below."}
      </div>
      <label className="text-xs text-white/60 block">Facebook Page ID<input value={f.fb_page_id || ""} onChange={e => setF({ ...f, fb_page_id: e.target.value })} className={`${inp} w-full mt-1`} data-testid="fb-page-id" /></label>
      <label className="text-xs text-white/60 block">Page Access Token (long-lived; needs pages_manage_posts, pages_read_engagement, read_insights)<input value={f.fb_page_token || ""} onChange={e => setF({ ...f, fb_page_token: e.target.value })} className={`${inp} w-full mt-1`} data-testid="fb-page-token" placeholder="EAAB…" /></label>
      <div className="grid grid-cols-3 gap-3">
        <label className="text-xs text-white/60">Time zone<input value={f.timezone || ""} onChange={e => setF({ ...f, timezone: e.target.value })} className={`${inp} w-full mt-1`} data-testid="tz" /></label>
        <label className="text-xs text-white/60">Default post hour (0-23)<input type="number" min="0" max="23" value={f.post_hour} onChange={e => setF({ ...f, post_hour: e.target.value })} className={`${inp} w-full mt-1`} data-testid="post-hour" /></label>
        <label className="text-xs text-white/60">Posts per week<input type="number" min="1" max="7" value={f.posts_per_week} onChange={e => setF({ ...f, posts_per_week: e.target.value })} className={`${inp} w-full mt-1`} data-testid="posts-per-week" /></label>
      </div>
      <label className="text-xs text-white/60 block">Public site URL (used in links & image URLs sent to Facebook)<input value={f.site_url || ""} onChange={e => setF({ ...f, site_url: e.target.value })} className={`${inp} w-full mt-1`} data-testid="site-url" /></label>
      <button type="button" className={gold} onClick={save} data-testid="settings-save"><Save size={12} /> Save settings</button>
    </div>
  );
}
